import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MusicNoteRounded from '@mui/icons-material/MusicNoteRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import { useJamRoom } from '../hooks/useJamRoom.jsx';

function readStored(key, fallback = '') {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/** Puerta de entrada: nombre de usuario + codigo de sala (opcional). */
export default function JoinGate() {
  const jam = useJamRoom();
  const [username, setUsername] = useState(() => readStored('jam:username'));
  const [roomId, setRoomId] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('room');
    return fromUrl || readStored('jam:roomId');
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const offline = jam.socketStatus === 'error';

  async function submit(e) {
    e.preventDefault();
    const name = username.trim();
    if (!name) {
      setError('Escribe tu nombre para entrar a la sala.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await jam.join({ username: name, roomId: roomId.trim() || null });
    } catch (err) {
      setError(err.message || 'No se pudo entrar a la sala.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      component="main"
      sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 2, py: 6, overflowY: 'auto' }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 460,
          p: { xs: 3, sm: 4.5 },
          borderRadius: '28px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(30,31,32,0.86)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                borderRadius: '18px',
                bgcolor: 'rgba(168,199,250,0.16)',
                color: 'primary.main',
              }}
            >
              <MusicNoteRounded fontSize="large" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5">Sala Jam</Typography>
              <Typography variant="body2" color="text.secondary">
                Cola colaborativa de música en tiempo real.
              </Typography>
            </Box>
          </Stack>

          <Box component="form" onSubmit={submit} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Tu nombre"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Cómo te ven los demás"
                autoComplete="nickname"
                autoFocus
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="Código de sala (opcional)"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="Vacío = crear una sala nueva"
                spellCheck={false}
                fullWidth
                helperText="Si te invitaron, pega aquí el código."
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkRounded fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy || jam.socketStatus === 'connecting'}
                sx={{ mt: 1 }}
              >
                {busy ? 'Entrando…' : 'Unirme a la Jam'}
              </Button>
            </Stack>
          </Box>

          {(error || offline || jam.socketError) && (
            <Alert severity="error">
              {error ||
                'No se alcanza el servidor. Comprueba que el backend esté en marcha (npm run dev en /backend).'}
            </Alert>
          )}

          {!offline && (
            <Typography variant="caption" color="text.secondary">
              Si entras primero, controlas el reproductor como anfitrión hasta que salgas.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
